import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faTimes,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import AudioPlayer from 'components/audio/AudioPlayerQA2';
import {
  HVARiskCallValue,
  reviewCallRisks,
  fetchCallDetails
} from 'services/hvarisk.service';
import { format } from 'date-fns';

interface RiskReviewModalProps {
  show: boolean;
  onHide: () => void;
  risk: HVARiskCallValue;
  currentUserId: number;
  onReviewComplete: () => void;
}

const RiskReviewModal: React.FC<RiskReviewModalProps> = ({
  show,
  onHide,
  risk,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  currentUserId,
  onReviewComplete
}) => {
  const [qaRiskValue, setQaRiskValue] = useState<number>(
    risk.qariskvalue || risk.iariskvalue
  );
  const [comment, setComment] = useState<string>(risk.qaagentcoment || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [callDetails, setCallDetails] = useState<any>(null);
  const [isLoadingCall, setIsLoadingCall] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      await reviewCallRisks(risk.callid, [
        {
          riskelementid: risk.riskelementid,
          qariskvalue: qaRiskValue,
          qaagentcoment: comment
        }
      ]);

      onReviewComplete();
    } catch (err) {
      setError('Error saving review. Please try again.');
      console.error('Error reviewing risk:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRiskBadgeVariant = (value: number): string => {
    if (value <= 2) return 'success';
    if (value === 3) return 'warning';
    return 'danger';
  };

  const getRiskLevelText = (value: number): string => {
    if (value <= 2) return 'Low';
    if (value === 3) return 'Medium';
    if (value === 4) return 'High';
    return 'Critical';
  };

  const riskLabels = [
    { value: 1, label: 'Very Low', color: 'success' },
    { value: 2, label: 'Low', color: 'success' },
    { value: 3, label: 'Medium', color: 'warning' },
    { value: 4, label: 'High', color: 'danger' },
    { value: 5, label: 'Critical', color: 'danger' }
  ];

  useEffect(() => {
    const loadCallDetails = async () => {
      if (show && risk.callid) {
        try {
          setIsLoadingCall(true);
          const details = await fetchCallDetails(risk.callid);
          setCallDetails(details);
        } catch (error) {
          console.error('Error loading call details:', error);
          setError('Error loading call details');
        } finally {
          setIsLoadingCall(false);
        }
      }
    };

    loadCallDetails();
  }, [show, risk.callid]);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Risk Element Review
          <span className="fs-9 text-body-quaternary fw-normal">
            {' ('}Call ID: <span className="mb-0 fw-bold">{risk.callid}</span>
            {' - '} Detection Date:{' '}
            <span className="mb-0 fw-bold">
              {new Date(risk.iariskvaluedate).toLocaleString()}
            </span>
            {')'}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-6 pt-5 pb-5">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Risk Details */}
        <div className="mb-2">
          <div className="border rounded p-3 bg-body">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <p className="mb-1 fw-bold">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="me-2"
                  />
                  {risk.hvariskelement?.name}
                </p>
                <p className="mb-0 text-muted fs-9">
                  <span className="fw-bold">Category:</span>{' '}
                  {risk.hvariskelement?.hvariskcategory?.name}
                </p>
                {risk.hvariskelement?.description && (
                  <p className="mb-0 fs-9">{risk.hvariskelement.description}</p>
                )}
              </div>
              <Badge
                pill
                bg={getRiskBadgeVariant(risk.iariskvalue)}
                className="ms-2"
              >
                AI: {getRiskLevelText(risk.iariskvalue)} ({risk.iariskvalue})
              </Badge>
            </div>
          </div>
        </div>

        {/* Audio */}
        {isLoadingCall ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 mb-0 text-muted">Loading call details...</p>
          </div>
        ) : callDetails ? (
          <AudioPlayer
            callid={risk.callid || ''}
            campaignid={callDetails.campaignid || 0}
            customer={callDetails.customerfullname || ''}
            phone={
              callDetails.calldatetime
                ? format(
                    new Date(callDetails.calldatetime),
                    'MMM dd, yyyy - HH:mm:ss'
                  ) +
                  ' (Phone number: ' +
                  callDetails.phonenumber +
                  ')'
                : callDetails.phonenumber || ''
            }
            audioSrc={callDetails.audiosource || ''}
          />
        ) : (
          <div className="alert alert-warning mb-3">
            <p className="mb-0">Unable to load call details</p>
          </div>
        )}

        {/* Manual Review Section */}
        <div className="mt-2 mb-0 rounded p-3 border bg-body">
          <h5 className="mb-3">Manual Risk Assessment</h5>

          <div className="mb-1">
            <div className="btn-group d-flex mt-2" role="group">
              {riskLabels.map(({ value, label, color }) => (
                <Button
                  key={value}
                  size="sm"
                  variant={qaRiskValue === value ? color : 'phoenix-secondary'}
                  onClick={() => setQaRiskValue(value)}
                  className="flex-grow-1"
                >
                  <div>
                    <div className="fw-bold lh-1">
                      {value}
                      <small className="fs-10">
                        {' ('}
                        {label} {')'}
                      </small>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </div>

          <Form.Group className="mb-2">
            <Form.Control
              as="textarea"
              rows={2}
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Enter your review comments here..."
              className="fs-9"
            />
          </Form.Group>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          <FontAwesomeIcon icon={faSave} className="me-2" />
          {isSubmitting ? 'Saving...' : 'Save Review'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RiskReviewModal;
